import { useState } from "react";
import { toast } from "sonner";
import {
    Dialog,
    DialogContent,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import {
    Loader2,
    ShieldCheck,
    AlertTriangle,
    Star,
    Clock,
} from "lucide-react";
import { releaseDeposit } from "../lib/api";

const formatMoney = (n) =>
    (Number(n) || 0).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 2,
    });

const DEPOSIT_DECISION = {
    modal: "deposit-decision-modal",
    release: "deposit-decision-release",
    withhold: "deposit-decision-withhold",
    skip: "deposit-decision-skip",
};

/**
 * Shown to the OWNER immediately after they accept a return. Lets them
 * pick what to do with the deposit before moving on to the review step.
 *
 *   Accept return → this modal → (release | withhold | skip) → review modal
 */
const DepositDecisionModal = ({
    rental,
    open,
    onOpenChange,
    onReleased,
    onOpenWithhold,
    onSkip,
}) => {
    const [releasing, setReleasing] = useState(false);
    if (!rental) return null;

    const deposit = Number(rental.deposit_amount) || 0;
    // If the deposit was already resolved on a prior visit, we still show
    // the modal so the owner sees the review prompt — but hide the release
    // button when it's already been done.
    const alreadyResolved =
        rental.deposit_status === "released" ||
        rental.deposit_status === "withheld" ||
        rental.deposit_status === "partial_withheld";

    const handleRelease = async () => {
        try {
            setReleasing(true);
            const updated = await releaseDeposit(rental.id);
            toast.success("Deposit released", {
                description: updated.deposit_refund_id
                    ? "Stripe refund issued — funds settle in 5–10 business days."
                    : "Your renter has been notified.",
            });
            onReleased?.(updated);
        } catch (err) {
            toast.error("Couldn't release deposit", {
                description:
                    err?.response?.data?.detail || "Try again in a moment.",
            });
        } finally {
            setReleasing(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid={DEPOSIT_DECISION.modal}
                className="max-w-md rounded-3xl border border-[#2e3220]/8 bg-[#F4F1EA] p-6"
            >
                <DialogTitle className="font-display text-xl font-bold text-[#2e3220]">
                    Return accepted — now what about the deposit?
                </DialogTitle>
                <DialogDescription className="mt-1 text-sm text-[#5a6142]">
                    {alreadyResolved ? (
                        <>
                            Deposit is already{" "}
                            <strong>{rental.deposit_status?.replace("_", " ")}</strong>.
                            You can still leave a review.
                        </>
                    ) : (
                        <>
                            {rental.renter_name || "Your renter"} paid a{" "}
                            <strong>{formatMoney(deposit)}</strong> deposit.
                            Choose what to do with it. This decides on the spot —
                            no need to hunt for buttons later.
                        </>
                    )}
                </DialogDescription>

                <div className="mt-5 space-y-2.5">
                    {!alreadyResolved && (
                        <>
                            <button
                                type="button"
                                data-testid={DEPOSIT_DECISION.release}
                                onClick={handleRelease}
                                disabled={releasing}
                                className="flex w-full items-start gap-3 rounded-2xl border-2 border-[#737a3d] bg-[#737a3d] px-4 py-3 text-left text-[#F4F1EA] transition-transform hover:scale-[1.01] disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[#F4F1EA]/15">
                                    {releasing ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <ShieldCheck className="h-4 w-4" strokeWidth={2.5} />
                                    )}
                                </span>
                                <span className="min-w-0">
                                    <span className="block text-sm font-bold">
                                        Release the full {formatMoney(deposit)}
                                    </span>
                                    <span className="mt-0.5 block text-xs text-[#F4F1EA]/85">
                                        Item looks great · Refund to renter
                                    </span>
                                </span>
                            </button>
                            <button
                                type="button"
                                data-testid={DEPOSIT_DECISION.withhold}
                                onClick={() => onOpenWithhold?.(rental)}
                                className="flex w-full items-start gap-3 rounded-2xl border-2 border-[#B56C5A]/40 bg-[#F4F1EA] px-4 py-3 text-left text-[#7a3a30] transition-colors hover:bg-[#B56C5A]/8"
                            >
                                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[#B56C5A]/15 text-[#B56C5A]">
                                    <AlertTriangle className="h-4 w-4" strokeWidth={2.5} />
                                </span>
                                <span className="min-w-0">
                                    <span className="block text-sm font-bold">
                                        Withhold some or all
                                    </span>
                                    <span className="mt-0.5 block text-xs text-[#7a3a30]/80">
                                        Something&apos;s off · Deduct for damage or late return
                                    </span>
                                </span>
                            </button>
                        </>
                    )}
                    <button
                        type="button"
                        data-testid={DEPOSIT_DECISION.skip}
                        onClick={() => onSkip?.(rental)}
                        className="flex w-full items-start gap-3 rounded-2xl border border-[#2e3220]/12 bg-transparent px-4 py-3 text-left text-[#5a6142] transition-colors hover:bg-[#E8E2D0]/40"
                    >
                        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[#2e3220]/6">
                            {alreadyResolved ? (
                                <Star className="h-4 w-4" strokeWidth={2} />
                            ) : (
                                <Clock className="h-4 w-4" strokeWidth={2} />
                            )}
                        </span>
                        <span className="min-w-0">
                            <span className="block text-sm font-bold text-[#2e3220]">
                                {alreadyResolved
                                    ? "Continue to review"
                                    : "Decide later — go leave a review"}
                            </span>
                            <span className="mt-0.5 block text-xs text-[#5a6142]">
                                {alreadyResolved
                                    ? "Rate how it went"
                                    : "You can find this in Previously Lent anytime"}
                            </span>
                        </span>
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default DepositDecisionModal;
